import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ShieldCheck, ShieldAlert, Clock, Upload, AlertTriangle } from 'lucide-react';

export function KYCSubmission() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);
  const [uploadingSelfie, setUploadingSelfie] = useState(false);
  const [nin, setNin] = useState('');
  const [idImageUrl, setIdImageUrl] = useState('');
  const [selfieUrl, setSelfieUrl] = useState('');
  const [kycStatus, setKycStatus] = useState('unverified');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadKycStatus();
  }, []);

  const loadKycStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('kyc_status, kyc_id_image_url, kyc_selfie_url, kyc_rejection_reason')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      if (data) {
        setKycStatus(data.kyc_status || 'unverified');
        setIdImageUrl(data.kyc_id_image_url || '');
        setSelfieUrl(data.kyc_selfie_url || '');
        setRejectionReason(data.kyc_rejection_reason || '');
      }
    } catch (error) {
      console.error('Error loading KYC status:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file: File, type: 'id' | 'selfie') => {
    const setter = type === 'id' ? setUploadingId : setUploadingSelfie;
    setter(true);

    try {
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File must be under 5MB');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const ext = file.name.split('.').pop();
      const path = `${user.id}/${type}-${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('kyc-documents')
        .upload(path, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get signed URL since bucket is private
      const { data: signedData } = await supabase.storage
        .from('kyc-documents')
        .createSignedUrl(path, 60 * 60 * 24 * 365); // 1 year

      const url = signedData?.signedUrl || path;

      if (type === 'id') setIdImageUrl(url);
      else setSelfieUrl(url);

      toast({ title: 'File uploaded', description: `${type === 'id' ? 'ID image' : 'Selfie'} uploaded successfully` });
    } catch (error: any) {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
    } finally {
      setter(false);
    }
  };

  const handleSubmit = async () => {
    if (!nin || nin.length !== 11 || !/^\d{11}$/.test(nin)) {
      toast({ title: 'Invalid NIN', description: 'NIN must be exactly 11 digits', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('submit_kyc', {
        p_nin: nin,
        p_id_image_url: idImageUrl || null,
        p_selfie_url: selfieUrl || null,
      });

      if (error) throw error;

      setKycStatus('pending');
      setNin('');
      toast({ title: 'KYC Submitted', description: 'Your verification is pending admin review.' });
    } catch (error: any) {
      toast({ title: 'Submission failed', description: error.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const statusConfig: Record<string, { icon: any; badge: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    unverified: { icon: ShieldAlert, badge: 'Unverified', variant: 'destructive' },
    pending: { icon: Clock, badge: 'Under Review', variant: 'secondary' },
    approved: { icon: ShieldCheck, badge: 'Verified', variant: 'default' },
    rejected: { icon: AlertTriangle, badge: 'Rejected – Resubmit Required', variant: 'destructive' },
  };

  const config = statusConfig[kycStatus] || statusConfig.unverified;
  const StatusIcon = config.icon;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <StatusIcon className="h-5 w-5" />
              Identity Verification (KYC)
            </CardTitle>
            <CardDescription>
              Verify your identity to enable withdrawals
            </CardDescription>
          </div>
          <Badge variant={config.variant}>{config.badge}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {kycStatus === 'approved' && (
          <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Your identity has been verified. You can request withdrawals.
            </p>
          </div>
        )}

        {kycStatus === 'rejected' && (
          <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-sm text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Rejected: {rejectionReason || 'No reason provided'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">You can resubmit your KYC below.</p>
          </div>
        )}

        {kycStatus === 'pending' && (
          <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
            <p className="text-sm text-yellow-700 dark:text-yellow-400 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Your KYC is under review. This usually takes 1-2 business days.
            </p>
          </div>
        )}

        {(kycStatus === 'unverified' || kycStatus === 'rejected') && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nin">National Identification Number (NIN) *</Label>
              <Input
                id="nin"
                value={nin}
                onChange={(e) => setNin(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="Enter your 11-digit NIN"
                maxLength={11}
              />
              <p className="text-xs text-muted-foreground">{nin.length}/11 digits</p>
            </div>

            <div className="space-y-2">
              <Label>ID Document Image (optional)</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={uploadingId}
                  onClick={() => document.getElementById('kyc-id-upload')?.click()}
                >
                  {uploadingId ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Upload ID
                </Button>
                {idImageUrl && <span className="text-xs text-green-600">✓ Uploaded</span>}
              </div>
              <input
                id="kyc-id-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], 'id')}
              />
            </div>

            <div className="space-y-2">
              <Label>Selfie (optional)</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={uploadingSelfie}
                  onClick={() => document.getElementById('kyc-selfie-upload')?.click()}
                >
                  {uploadingSelfie ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Upload Selfie
                </Button>
                {selfieUrl && <span className="text-xs text-green-600">✓ Uploaded</span>}
              </div>
              <input
                id="kyc-selfie-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], 'selfie')}
              />
            </div>

            <Button onClick={handleSubmit} disabled={submitting || nin.length !== 11} className="w-full">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit KYC'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
